import styled from '@emotion/styled'
import type { FC, PropsWithChildren } from 'react'
import { forwardRef } from 'react'
import { m } from 'framer-motion'

import ButtonText from '@segment/matcha/components/ButtonText'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'
import Image from '@segment/matcha/components/Image'

import CustomerCardBase, * as CustomerCard from '@segment/matcha/recipes/CustomerCard'

import useContentfulImage from '@segment/utils/useContentfulImage'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import type { Card } from './Hero'
import { useMobileListCarousel } from './useMobileListCarousel'
import { featuredImageFormats, logoImageFormats } from './imageFormats'

interface MobileListProps {
  data: Card[]
}

export const MobileListCarousel: FC<PropsWithChildren<MobileListProps>> = ({ data }) => {
  const { page, animation, ref, isLargeCardResolution, handleDragEnd, handleDragStart, items } = useMobileListCarousel({
    data,
  })

  return (
    <Wrapper
      key={page}
      drag="x"
      dragDirectionLock={true}
      dragConstraints={{ left: 0, right: 0 }}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      variants={{
        toLeft: {
          x: isLargeCardResolution ? `-368px` : `-308px`,
          pointerEvents: `none`,
        },
        toRight: {
          x: isLargeCardResolution ? `368px` : `308px`,
          pointerEvents: `none`,
        },
      }}
      animate={animation}
      dragMomentum={false}
      transition={{
        x: { type: `spring`, mass: 0.5, stiffness: 500, damping: 50 },
      }}
    >
      {data[items.prevGhost] && <Item key={`prevGhost-${items.prevGhost}`} data={data[items.prevGhost]} />}
      {data[items.prev] && <Item key={`prev-${items.prev}`} data={data[items.prev]} />}
      {data[items.current] && (
        <Item ref={ref} active={true} key={`current-${items.current}`} data={data[items.current]} />
      )}
      {data[items.next] && <Item key={`next-${items.next}`} data={data[items.next]} />}
      {data[items.nextGhost] && <Item key={`nextGhost-${items.nextGhost}`} data={data[items.nextGhost]} />}
    </Wrapper>
  )
}

interface ItemProps {
  data: Card
  active?: boolean
}
const Item = forwardRef<HTMLDivElement, ItemProps>(({ data, active, ...rest }, ref) => {
  const pageConfig = usePageConfig()
  const logoFile = data?.featuredLogo?.fields?.file
  const [logoOptimizedUrl] = useContentfulImage(logoFile?.url, logoImageFormats) ?? []
  const imageFile = data?.featuredImage?.fields?.file
  const [featuredImageOptimizedUrl] = useContentfulImage(imageFile?.url, featuredImageFormats) ?? []

  return (
    <StyledItem ref={ref} {...rest}>
      <Cards
        tag={active ? `a` : `div`}
        href={getAbsoluteUrl(`/customers/${data.slug}/`, pageConfig)}
        img={featuredImageOptimizedUrl}
        hoverable={true}
      >
        <CustomerCard.Row>
          {logoOptimizedUrl && (
            <Logo
              loader={() => logoOptimizedUrl}
              width={logoImageFormats.width}
              height={logoImageFormats.height}
              src={logoOptimizedUrl}
              alt="Customer Card"
            />
          )}
        </CustomerCard.Row>
        <CustomerCard.Row>
          <ButtonText size="small" variant="mono" iconEnd={ChevronRightIcon} appearance="dark">
            Read their story
          </ButtonText>
        </CustomerCard.Row>
      </Cards>
    </StyledItem>
  )
})

const Logo = styled(Image)`
  max-width: 130px;
  width: auto;
  height: 30px;
  margin: 0;
`

const Cards = styled(CustomerCardBase)`
  height: 100%;

  ${({ theme }) => theme.breakpoints.up(`sm`)} {
    height: 100%;
  }
`

const Wrapper = styled(m.div)`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: min-content;
  gap: 8px;
  margin-left: -600px;
  padding: 0 24px;
  scrollbar-width: none;

  ::-webkit-scrollbar {
    display: none;
  }

  ${({ theme }) => theme.breakpoints.up(`sm`)} {
    padding: 0 40px;
    margin-left: -720px;
  }

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    display: none;
  }
`

const StyledItem = styled.div`
  width: 300px;
  height: 246px;

  ${({ theme }) => theme.breakpoints.up(`sm`)} {
    width: 360px;
    height: 280px;
  }
`
